import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { bundleItemChip } from '../../shared/bundleChip';
import type { ScriptStatus } from '../../shared/types';
import type { BundleReviewContextValue, BundleReviewReadyItem } from '../lib/bundleReviewContext';
import { useAnchoredMenu } from './useAnchoredMenu';

/** The slice of ClientReview's live script the picker overlays on the bundle
 *  snapshot, so approving or sending feedback on this page updates the badge,
 *  the current row and the count without a navigation. */
export interface LiveCurrentScript {
  status: ScriptStatus;
  feedback_submitted_at: number | null;
}

/** Value of the phone select's "All scripts" option; never a script slug. */
const HUB_OPTION = '__all_scripts__';

/** Tailwind's `md` breakpoint: the desktop trigger and menu exist from here up. */
const DESKTOP_QUERY = '(min-width: 48rem)';

/**
 * The bundle snapshot in BundleReviewContext is fetched per navigation, but
 * ClientReview refetches its own script after an approval or a feedback
 * submission. Overlay that live state on the current item so the picker
 * never disagrees with the hero sitting right under it.
 */
export function withLiveCurrent(
  items: BundleReviewReadyItem[],
  currentSlug: string,
  live: LiveCurrentScript | undefined,
): BundleReviewReadyItem[] {
  if (!live) return items;
  return items.map((item) => (item.slug === currentSlug ? { ...item, ...live } : item));
}

export function pickerStatus(item: BundleReviewReadyItem) {
  // Important utilities override the shared menu's unlayered gloss colour.
  if (item.status === 'approved') return { word: 'Approved', tone: 'text-accent!' };
  const chip = bundleItemChip(item);
  return { word: chip.label, tone: chip.tone === 'accent' ? 'text-gold-deep!' : '' };
}

export function pickerCounts(items: BundleReviewReadyItem[]) {
  return {
    approvedCount: items.filter((item) => item.status === 'approved').length,
    total: items.length,
  };
}

function Caret({ open = false }: { open?: boolean }) {
  return (
    <svg aria-hidden width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-faint shrink-0">
      <path d={open ? 'M3 7.5l3-3 3 3' : 'M3 4.5l3 3 3-3'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Tick({ size }: { size: number }) {
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M3 7.5l2.6 2.6L11 4.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function BundleScriptPicker({
  bundleReview,
  currentSlug,
  currentScript,
}: {
  bundleReview: BundleReviewContextValue;
  currentSlug: string;
  currentScript?: LiveCurrentScript;
}) {
  const navigate = useNavigate();
  const menu = useAnchoredMenu({ width: 340, align: 'left' });
  const { bundleSlug, bundleTitle } = bundleReview;
  const items = withLiveCurrent(bundleReview.items, currentSlug, currentScript);

  // The desktop menu is display:none below md, but the hook's document
  // listeners would stay live. If the window crosses the breakpoint while the
  // menu is open, close it rather than leave an invisible menu eating keys.
  // `close` is recreated every render and a resize re-renders (the hook
  // repositions the menu), so the subscription must not depend on it, and the
  // breakpoint is checked at subscribe time as well: the resize that crosses
  // it can re-run this effect before the MediaQueryList's change event fires.
  const menuOpen = menu.open;
  const closeRef = useRef(menu.close);
  closeRef.current = menu.close;
  useEffect(() => {
    if (!menuOpen) return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const closeIfPhone = () => {
      if (!mq.matches) closeRef.current();
    };
    closeIfPhone();
    mq.addEventListener('change', closeIfPhone);
    return () => mq.removeEventListener('change', closeIfPhone);
  }, [menuOpen]);
  const currentIndex = items.findIndex((item) => item.slug === currentSlug);
  const current = items[currentIndex];
  const currentTitle = current?.title ?? '';
  const n = currentIndex + 1;
  const { approvedCount, total: m } = pickerCounts(items);
  const countTone = current?.status === 'approved' ? 'bg-accent text-accent-fg' : 'bg-fg text-page';

  return (
    <div data-print-hide className="relative min-w-0">
      <div className="hidden md:flex min-w-0">
        <button
          ref={menu.anchorRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={menu.open}
          title={currentTitle}
          aria-label={`Script ${n} of ${m}: ${currentTitle}. Choose a script`}
          onClick={menu.toggle}
          className={`inline-flex items-center gap-2 h-[30px] pl-1 pr-2.5 rounded-full bg-card border text-fg text-[13px] font-semibold min-w-0 max-w-full transition-colors hover:border-faint/60 hover:bg-elevated focus:outline-none focus-visible:shadow-focus ${menu.open ? 'border-accent shadow-focus' : 'border-line'}`}
        >
          <span className={`shrink-0 inline-flex items-center h-[22px] px-2 rounded-full font-mono text-[10px] font-bold tracking-[0.06em] ${countTone}`}>
            {n} / {m}
          </span>
          <span className="min-w-0 truncate max-w-[160px] lg:max-w-[260px]">{currentTitle}</span>
          <Caret open={menu.open} />
        </button>
      </div>

      {menu.open && menu.popupStyle && createPortal(
        <div
          ref={menu.menuRef}
          role="menu"
          className="brief-menu hidden md:block"
          style={menu.popupStyle}
          aria-label="Scripts in this bundle"
          data-print-hide
        >
          <div className="brief-menu__head flex items-center justify-between gap-2">
            <span className="truncate">{bundleTitle}</span>
            <span className="shrink-0 text-accent">{approvedCount} of {m} approved</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.map((item, i) => {
              const isCurrent = item.slug === currentSlug;
              const approved = item.status === 'approved';
              const status = pickerStatus(item);
              return (
                <Link
                  key={item.slug}
                  role="menuitem"
                  to={`/b/${bundleSlug}/${item.slug}`}
                  className={`brief-menu__item${isCurrent ? ' brief-menu__item--checked' : ''}`}
                  aria-current={isCurrent ? 'page' : undefined}
                  onClick={() => menu.close()}
                >
                  <span aria-hidden className={`shrink-0 w-[22px] h-[22px] rounded-full inline-flex items-center justify-center font-mono text-[10px] font-bold ${approved ? 'bg-accent-glow-soft text-accent' : isCurrent ? 'bg-fg text-page' : 'bg-elevated text-muted'}`}>
                    {approved ? <Tick size={11} /> : i + 1}
                  </span>
                  <span className="brief-menu__body">
                    <span className="brief-menu__name truncate">{item.title}</span>
                    <span className={`brief-menu__gloss ${status.tone}`}>{status.word}</span>
                  </span>
                  {isCurrent && <span className="brief-menu__hint text-accent" aria-hidden><Tick size={14} /></span>}
                </Link>
              );
            })}
          </div>
          <div className="brief-menu__sep" />
          <Link role="menuitem" to={`/b/${bundleSlug}`} className="brief-menu__item" onClick={() => menu.close()}>
            <span aria-hidden className="shrink-0 w-[22px] inline-flex justify-center text-faint">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
                <rect x="1.5" y="1.5" width="4.5" height="4.5" rx="1" />
                <rect x="8" y="1.5" width="4.5" height="4.5" rx="1" />
                <rect x="1.5" y="8" width="4.5" height="4.5" rx="1" />
                <rect x="8" y="8" width="4.5" height="4.5" rx="1" />
              </svg>
            </span>
            <span className="brief-menu__body"><span className="brief-menu__name text-muted!">All scripts</span></span>
          </Link>
        </div>,
        document.body,
      )}

      <div className="relative md:hidden">
        <div aria-hidden className="inline-flex items-center gap-2 h-[28px] pl-1 pr-2.5 rounded-full bg-card border border-line text-fg">
          <span className={`shrink-0 inline-flex items-center h-[20px] px-2 rounded-full font-mono text-[10px] font-bold tracking-[0.06em] ${countTone}`}>
            {n} / {m}
          </span>
          <Caret />
        </div>
        <select
          aria-label="Jump to script"
          value={currentSlug}
          onChange={(e) =>
            navigate(e.target.value === HUB_OPTION ? `/b/${bundleSlug}` : `/b/${bundleSlug}/${e.target.value}`)
          }
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        >
          {items.map((item, i) => (
            <option key={item.slug} value={item.slug}>
              {item.status === 'approved' ? '✓ ' : ''}
              Script {i + 1}: {item.title}
            </option>
          ))}
          {/* Phones have no menu, so the hub route lives here instead. */}
          <option value={HUB_OPTION}>All scripts</option>
        </select>
      </div>
    </div>
  );
}
